import { useMemo, useState } from "react";
import { useRouter } from "@/lib/nav";
import type { PublicAttendee } from "@/lib/views";
import { splitSeat } from "@/lib/types";

/**
 * 「查自己的」：打名字就看得到自己坐哪一桌。
 *
 * 活動當天最常見的一句是「我坐哪？」——原本只有一條路：翻出當初那個
 * 登機證連結。連結丟了就只能去問主辦人，73 個人問一輪就是一場災難。
 *
 * 名字與座位本來就是公開的（座位圖上點任何一個位子都看得到是誰），
 * 所以這裡不需要任何新的 API，也沒有多暴露任何東西 ——
 * 純粹是把頁面上已經有的資料換一個查法。
 *
 * 刻意「不」用名字換出登機證連結：那個連結就是授權本身，
 * 誰拿到都能代替本人報到、改位子、用他的身分發言。
 * 名字是全場都知道的東西，不能當成密碼用。
 */
export function SelfLookup({ attendees }: { attendees: PublicAttendee[] }) {
  const [query, setQuery] = useState("");
  const router = useRouter();

  const q = query.trim();
  // 登機證代碼長這樣：DN08122-NF8U4XDH。有連字號就當成是在輸入代碼
  const looksLikeCode = /-/.test(q) && q.length >= 8;

  const matches = useMemo(() => {
    if (looksLikeCode || q.length < 1) return [];
    const needle = q.toLowerCase();
    return attendees.filter((a) => a.name.toLowerCase().includes(needle)).slice(0, 8);
  }, [attendees, q, looksLikeCode]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (looksLikeCode) router.push(`/pass/${q.toUpperCase()}`);
  };

  return (
    // 這一塊原本只是頁尾一個灰底輸入框，跟畫布同色、看起來像沒作用的裝飾。
    // 做成有標題的卡片：它是一個獨立的功能，不是表單的附註。
    <div className="card mt-5 p-5">
      <div className="text-accent text-[12px] font-semibold">已經報名了？</div>
      <h2 className="mt-0.5 text-[17px] font-bold tracking-tight">查自己坐哪一桌</h2>

      <form onSubmit={submit} className="mt-3 flex gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="打你的名字…"
          aria-label="查自己的座位"
          className="field-input border-line flex-1 text-[15px]"
        />
        <button
          type="submit"
          disabled={!looksLikeCode}
          className="btn btn-secondary px-4 py-2.5 text-[14px] disabled:opacity-40"
        >
          查看
        </button>
      </form>

      {q.length >= 1 && !looksLikeCode && (
        <div className="mt-3">
          {matches.length === 0 ? (
            <p className="text-sub text-[13px]">
              找不到「{q}」——確認一下跟報名時打的名字一樣，或還沒報名。
            </p>
          ) : (
            <ul className="border-line divide-line divide-y overflow-hidden rounded-xl border">
              {matches.map((a) => {
                const table = a.seat ? splitSeat(a.seat).table : undefined;
                return (
                  <li key={a.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <span className="min-w-0 truncate text-[14px]">
                      <span className="font-semibold">{a.name}</span>
                      {a.industry && <span className="text-sub ml-2 text-[12px]">{a.industry}</span>}
                    </span>
                    {a.seat ? (
                      <span className="shrink-0 text-[13px]">
                        <span className="text-sub">第 {table} 桌</span>
                        <span className="text-accent ml-2 text-[15px] font-bold tabular-nums">
                          {a.seat}
                        </span>
                      </span>
                    ) : (
                      <span className="text-warn-deep shrink-0 text-[13px]">
                        {a.status === "standby" ? "候補中" : "還沒選位"}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* 一行就好：原本三行灰字會把整塊變成沒人讀的說明書 */}
      <p className="text-sub mt-3 text-[12px]">
        這裡只查座位。要拿回登機證 QR，貼上代碼（像 DN0812-XXXXXXXX）或用原本的連結。
      </p>
    </div>
  );
}
